import { Injectable } from '@angular/core';

/** One field the profile is not considered complete without. */
export interface ProfileField {
    /** The key as it appears on the stored customer object. */
    key: string;
    /** What the profile form calls it, for the "still needed" list. */
    label: string;
}

/**
 * Whether a signed-in customer's profile is filled in.
 *
 * One definition, used by the header badge and the checkout banner. Two
 * surfaces asking the same question have to get the same answer, or a customer
 * chases a dot that the page it leads to says nothing about.
 *
 * Applies to INDIAPICTURE and EXCLUSIVE accounts alike; the only thing that
 * differs between them is where the "complete it" link goes.
 */
@Injectable({
    providedIn: 'root'
})
export class ProfileCompletionService {

    /**
     * The fields that must be present.
     *
     * Matches what the profile form marks `Validators.required` — the customer
     * is being sent to that form, so asking for a field it does not collect
     * would be a prompt they could never satisfy.
     *
     * `address` and `customers_address_line1` are the same field: the form calls
     * its control `address1` and the API takes `address`, but the stored
     * customer carries `customers_address_line1`. It is listed once.
     */
    static readonly REQUIRED_FIELDS: ProfileField[] = [
        { key: 'customers_firstname',     label: 'First name' },
        { key: 'customers_lastname',      label: 'Last name' },
        { key: 'customers_telephone',     label: 'Phone' },
        { key: 'customers_address_line1', label: 'Address' },
        { key: 'customers_city',          label: 'City' },
        { key: 'customers_state',         label: 'State' },
        { key: 'customers_postcode',      label: 'Postcode' },
        { key: 'customers_country',       label: 'Country' },
        { key: 'company_type',            label: 'Company type' },
        { key: 'job_description',         label: 'Job description' }
    ];

    /**
     * Which required fields are still empty.
     *
     * Whitespace counts as empty: a space in the phone box is not a phone
     * number. The numeric guard matters for postcode, which can arrive as a
     * number rather than a string and would otherwise trip the trim().
     */
    missingFields(user: any): ProfileField[] {
        if (!user) { return []; }

        return ProfileCompletionService.REQUIRED_FIELDS.filter(field => {
            const value = user[field.key];

            if (value === null || value === undefined) { return true; }
            if (typeof value === 'number') { return false; }

            return String(value).trim() === '';
        });
    }

    /**
     * Whether to prompt this customer to finish their profile.
     *
     * Signed out is never incomplete — there is no profile to complete, and the
     * badge would be nonsense over a Sign in link.
     */
    isIncomplete(user: any): boolean {
        if (!user || !user.customers_id) { return false; }

        return this.missingFields(user).length > 0;
    }

    /**
     * Where "Complete profile" goes for this account.
     *
     * The two account areas are separate feature modules with separate profile
     * screens, so the link cannot be a constant.
     */
    profileLink(user: any): string {
        const isExclusive = user && (user.isExclusive === true || user.user_type === 'EXCLUSIVE');

        return isExclusive ? '/exclusive/account/account-information' : '/account/profile';
    }
}
